import { GameState, GameScreen, GameStateData, ComboResult } from '../game/GameState';
import { WizardType, WIZARD_CONFIGS } from '../wizards/WizardTypes';
import { CooldownManager, SpellType } from '../game/CooldownManager';
import { PlayerRole, RoomState, CheerType, EmoteType } from '../multiplayer/protocol';
import { EMOTE_CONFIGS } from '../social/EmoteDisplay';
import { FriendClient, FriendInfo, PendingRequest } from '../social/FriendClient';
import { copyRoomUrl, generateRoomId, navigateToRoom } from '../multiplayer/RoomManager';
import { GestureInfo, GestureShape } from '../gestures/GestureDetector';
import { TutorialManager, createGestureReferenceOverlay } from './TutorialManager';
import { AchievementManager } from '../progression/AchievementManager';
import { CosmeticManager, CosmeticType } from '../progression/CosmeticManager';
import { TournamentUI } from './TournamentUI';
import { ArenaThemeId, ARENA_THEMES } from '../scene/ArenaThemeManager';

export class UIManager {
  private container: HTMLElement;
  private gameState: GameState;
  private cooldownManager: CooldownManager;

  private menuScreen: HTMLElement | null = null;
  private wizardSelectScreen: HTMLElement | null = null;
  private hudElement: HTMLElement | null = null;
  private gameOverScreen: HTMLElement | null = null;
  private loadingScreen: HTMLElement | null = null;
  private gestureIndicator: HTMLElement | null = null;

  private ariaLiveRegion: HTMLElement | null = null;

  private selectedWizard: WizardType = 'archmage';
  private onStartGame: (() => void) | null = null;
  private onWizardSelect: ((type: WizardType) => void) | null = null;
  private onPlayAgain: (() => void) | null = null;
  private onMainMenu: (() => void) | null = null;
  private onInitCamera: (() => Promise<boolean>) | null = null;
  private onSetPracticeMode: ((enabled: boolean) => void) | null = null;
  private onShowAchievements: (() => void) | null = null;
  private onShowCosmetics: (() => void) | null = null;
  private onShowFriends: (() => void) | null = null;
  private onShowShop: (() => void) | null = null;
  private onArenaThemeChange: ((themeId: ArenaThemeId) => void) | null = null;
  private tournamentUI: TournamentUI | null = null;

  // Tutorial system
  private tutorialManager: TutorialManager;
  private gestureReferenceOverlay: HTMLElement | null = null;
  private gestureHelpBtn: HTMLElement | null = null;
  private onGestureConfirmSound: (() => void) | null = null;

  // Multiplayer UI elements
  private multiplayerLobby: HTMLElement | null = null;
  private spectatorOverlay: HTMLElement | null = null;
  private cheerButtons: HTMLElement | null = null;
  private onCheer: ((type: CheerType) => void) | null = null;

  // Social UI elements
  private emotePanel: HTMLElement | null = null;
  private friendListModal: HTMLElement | null = null;
  private onEmote: ((type: EmoteType) => void) | null = null;

  // Countdown overlay
  private countdownOverlay: HTMLElement | null = null;

  // Combo meter UI
  private comboMeterContainer: HTMLElement | null = null;
  private comboMeterFill: HTMLElement | null = null;
  private comboMeterBonus: HTMLElement | null = null;
  private comboMeterWrapper: HTMLElement | null = null;
  private comboPopup: HTMLElement | null = null;
  private comboPopupTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(gameState: GameState, cooldownManager: CooldownManager) {
    this.container = document.getElementById('ui-overlay')!;
    this.gameState = gameState;
    this.cooldownManager = cooldownManager;
    this.tutorialManager = new TutorialManager(this.container);

    this.createUI();
    this.gameState.subscribe(this.onStateChange.bind(this));
    this.setupEscapeKeyHandler();

    this.startCooldownUpdate();
  }

  private createUI(): void {
    this.createAriaLiveRegion();
    this.createLoadingScreen();
    this.createMenuScreen();
    this.createWizardSelectScreen();
    this.createHUD();
    this.createGameOverScreen();
    this.createGestureIndicator();
    this.createGestureReference();
    this.createCountdownOverlay();
    this.createComboUI();
  }

  private setupEscapeKeyHandler(): void {
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      const state = this.gameState.getState();

      // Close how-to-play modal if open
      const howToPlayModal = this.container.querySelector('#btn-close-help');
      if (howToPlayModal) {
        (howToPlayModal as HTMLElement).click();
        return;
      }

      // Close gesture reference overlay if open
      if (this.gestureReferenceOverlay && !this.gestureReferenceOverlay.classList.contains('hidden')) {
        this.gestureReferenceOverlay.classList.add('hidden');
        return;
      }

      // Navigate back from wizard-select to menu
      if (state.screen === 'wizard-select') {
        this.showScreen('menu');
        return;
      }

      // Navigate back from game-over to menu
      if (state.screen === 'game-over' && this.onMainMenu) {
        this.onMainMenu();
        return;
      }
    });
  }

  private createAriaLiveRegion(): void {
    this.ariaLiveRegion = document.createElement('div');
    this.ariaLiveRegion.id = 'game-announcements';
    this.ariaLiveRegion.setAttribute('role', 'status');
    this.ariaLiveRegion.setAttribute('aria-live', 'polite');
    this.ariaLiveRegion.setAttribute('aria-atomic', 'true');
    this.ariaLiveRegion.className = 'sr-only';
    document.body.appendChild(this.ariaLiveRegion);
  }

  /** Announce a game event for screen readers via the ARIA live region */
  announce(message: string): void {
    if (!this.ariaLiveRegion) return;
    // Clear and re-set to ensure the announcement fires even for repeated messages
    this.ariaLiveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = message;
      }
    });
  }

  private createLoadingScreen(): void {
    this.loadingScreen = document.createElement('div');
    this.loadingScreen.className = 'loading-screen';
    this.loadingScreen.innerHTML = `
      <h1 class="game-title">Wizard Duel</h1>
      <div class="loading-text">Preparing the Arena...</div>
      <div class="loading-bar-container">
        <div class="loading-bar"></div>
      </div>
    `;
    this.container.appendChild(this.loadingScreen);
  }

  private createMenuScreen(): void {
    this.menuScreen = document.createElement('div');
    this.menuScreen.className = 'menu-screen menu-screen--main hidden';
    this.menuScreen.setAttribute('role', 'main');
    this.menuScreen.setAttribute('aria-label', 'Main Menu');
    this.menuScreen.innerHTML = `
      <div class="menu-particles" aria-hidden="true"></div>
      <h1 class="game-title menu-title">Wizard Duel</h1>
      <div class="game-subtitle menu-subtitle">A Battle of Arcane Might</div>
      <img src="/images/wizard-duel-hero.png" alt="Two wizards in magical combat" class="hero-image" />
      <div class="ornament" aria-hidden="true"></div>
      <button class="menu-button menu-button--primary" id="btn-play" tabindex="0" aria-label="Begin Your Journey - Start single player game" style="--enter-delay: 0">Begin Your Journey</button>
      <div class="menu-button-group menu-button-group--actions">
        <button class="menu-button menu-button--action" id="btn-multiplayer" tabindex="0" aria-label="Multiplayer - Create a multiplayer room" style="--enter-delay: 1">Multiplayer</button>
        <button class="menu-button menu-button--action" id="btn-tournaments" tabindex="0" aria-label="Tournaments - Join bracket tournaments" style="--enter-delay: 2">Tournaments</button>
        <button class="menu-button menu-button--action" id="btn-tutorial" tabindex="0" aria-label="Tutorial - Learn gestures step by step" style="--enter-delay: 3">Tutorial</button>
      </div>
      <div class="menu-button-group menu-button-group--secondary">
        <button class="menu-button menu-button--secondary" id="btn-how-to-play" tabindex="0" aria-label="How to Play - Learn the controls" style="--enter-delay: 4">How to Play</button>
        <button class="menu-button menu-button--secondary" id="btn-achievements" tabindex="0" aria-label="Achievements - View your progress" style="--enter-delay: 5">Achievements</button>
        <button class="menu-button menu-button--secondary" id="btn-friends" tabindex="0" aria-label="Friends - Manage friends and quick-match" style="--enter-delay: 6">Friends</button>
        <button class="menu-button menu-button--secondary" id="btn-cosmetics" tabindex="0" aria-label="Cosmetics - Equip unlocked cosmetics" style="--enter-delay: 7">Cosmetics</button>
        <button class="menu-button menu-button--secondary" id="btn-shop" tabindex="0" aria-label="Shop - Spend Arcane Dust on cosmetics" style="--enter-delay: 8">Shop</button>
        <button class="menu-button menu-button--secondary" id="btn-replays" tabindex="0" aria-label="Replays - Watch match replays" style="--enter-delay: 9">Replays</button>
      </div>
    `;
    this.container.appendChild(this.menuScreen);

    this.menuScreen.querySelector('#btn-play')?.addEventListener('click', () => {
      this.showScreen('wizard-select');
    });

    this.menuScreen.querySelector('#btn-multiplayer')?.addEventListener('click', () => {
      const roomId = generateRoomId();
      navigateToRoom(roomId);
      window.location.reload();
    });

    this.menuScreen.querySelector('#btn-tutorial')?.addEventListener('click', () => {
      this.startTutorial();
    });

    this.menuScreen.querySelector('#btn-how-to-play')?.addEventListener('click', () => {
      this.showHowToPlay();
    });

    this.menuScreen.querySelector('#btn-achievements')?.addEventListener('click', () => {
      if (this.onShowAchievements) this.onShowAchievements();
    });

    this.menuScreen.querySelector('#btn-cosmetics')?.addEventListener('click', () => {
      if (this.onShowCosmetics) this.onShowCosmetics();
    });

    this.menuScreen.querySelector('#btn-friends')?.addEventListener('click', () => {
      if (this.onShowFriends) this.onShowFriends();
    });

    this.menuScreen.querySelector('#btn-tournaments')?.addEventListener('click', () => {
      this.showTournaments();
    });

    this.menuScreen.querySelector('#btn-shop')?.addEventListener('click', () => {
      if (this.onShowShop) this.onShowShop();
    });

    this.menuScreen.querySelector('#btn-replays')?.addEventListener('click', () => {
      // Delegate to the global replay list function set up in main.ts
      const win = window as unknown as { openReplayList?: () => void };
      if (win.openReplayList) {
        win.openReplayList();
      }
    });
  }

  private createWizardSelectScreen(): void {
    this.wizardSelectScreen = document.createElement('div');
    this.wizardSelectScreen.className = 'menu-screen hidden';
    this.wizardSelectScreen.setAttribute('role', 'main');
    this.wizardSelectScreen.setAttribute('aria-label', 'Wizard Selection');
    this.wizardSelectScreen.innerHTML = `
      <h2 class="game-title" style="font-size: 2.5rem;">Choose Your Wizard</h2>
      <div class="ornament" aria-hidden="true"></div>
      <div class="wizard-select" role="radiogroup" aria-label="Select a wizard">
        ${Object.values(WIZARD_CONFIGS).map((config) => `
          <div class="wizard-card" data-wizard="${config.type}" tabindex="0" role="radio"
               aria-checked="false" aria-label="${config.name}: ${config.description}">
            <div class="wizard-icon" aria-hidden="true">${config.icon}</div>
            <div class="wizard-name">${config.name}</div>
            <div class="wizard-desc">${config.description}</div>
          </div>
        `).join('')}
      </div>
      <h3 style="margin-top: 1.5rem; font-size: 1.2rem; color: #C9A227;">Arena Theme</h3>
      <div class="arena-theme-select" role="radiogroup" aria-label="Select an arena theme">
        ${Object.values(ARENA_THEMES).map((theme) => `
          <div class="arena-theme-card" data-theme="${theme.id}" tabindex="0" role="radio"
               aria-checked="false" aria-label="${theme.name}: ${theme.description}">
            <div class="arena-theme-name">${theme.name}</div>
            <div class="arena-theme-desc">${theme.description}</div>
          </div>
        `).join('')}
      </div>
      <button class="menu-button" id="btn-start-duel" tabindex="0" style="margin-top: 1rem;">Enter the Arena</button>
      <button class="menu-button" id="btn-back" tabindex="0" style="font-size: 1rem; padding: 0.8rem 2rem;">Back</button>
    `;
    this.container.appendChild(this.wizardSelectScreen);

    const cards = this.wizardSelectScreen.querySelectorAll('.wizard-card');

    const selectCard = (card: Element) => {
      cards.forEach(c => {
        c.classList.remove('selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('selected');
      card.setAttribute('aria-checked', 'true');
      this.selectedWizard = card.getAttribute('data-wizard') as WizardType;
      if (this.onWizardSelect) {
        this.onWizardSelect(this.selectedWizard);
      }
    };

    cards.forEach((card, index) => {
      // Click handler
      card.addEventListener('click', () => selectCard(card));

      // Keyboard handler for Enter/Space
      card.addEventListener('keydown', (e) => {
        const event = e as KeyboardEvent;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectCard(card);
        }
        // Arrow key navigation
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          const nextIndex = (index + 1) % cards.length;
          (cards[nextIndex] as HTMLElement).focus();
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          const prevIndex = (index - 1 + cards.length) % cards.length;
          (cards[prevIndex] as HTMLElement).focus();
        }
      });
    });

    // Select default (third card - Archmage)
    if (cards[2]) {
      selectCard(cards[2]);
    }

    // Arena theme selection
    const themeCards = this.wizardSelectScreen.querySelectorAll('.arena-theme-card');
    themeCards.forEach((tc) => {
      tc.addEventListener('click', () => {
        themeCards.forEach(c => {
          c.classList.remove('selected');
          c.setAttribute('aria-checked', 'false');
        });
        tc.classList.add('selected');
        tc.setAttribute('aria-checked', 'true');
        const themeId = tc.getAttribute('data-theme') as ArenaThemeId;
        if (this.onArenaThemeChange) {
          this.onArenaThemeChange(themeId);
        }
      });
    });

    // Select the saved theme by default
    this.selectDefaultThemeCard(themeCards);

    this.wizardSelectScreen.querySelector('#btn-start-duel')?.addEventListener('click', () => {
      if (this.onStartGame) {
        this.onStartGame();
      }
    });

    this.wizardSelectScreen.querySelector('#btn-back')?.addEventListener('click', () => {
      this.showScreen('menu');
    });
  }

  private createHUD(): void {
    this.hudElement = document.createElement('div');
    this.hudElement.className = 'hud';
    this.hudElement.style.display = 'none';
    this.hudElement.innerHTML = `
      <div class="player-stats">
        <div class="stats-name">You</div>
        <div class="health-bar-container" role="progressbar" aria-label="Your health" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" id="player-health-container">
          <div class="health-bar" id="player-health-bar"></div>
          <div class="health-text" id="player-health-text">100 / 100</div>
        </div>
      </div>
      <div class="enemy-stats">
        <div class="stats-name" style="text-align: right;">Enemy</div>
        <div class="health-bar-container" role="progressbar" aria-label="Enemy health" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" id="enemy-health-container">
          <div class="health-bar" id="enemy-health-bar"></div>
          <div class="health-text" id="enemy-health-text">100 / 100</div>
        </div>
      </div>
    `;
    this.container.appendChild(this.hudElement);
    // Spell buttons are created by Game.ts with cooldown overlays
  }

  private createGameOverScreen(): void {
    this.gameOverScreen = document.createElement('div');
    this.gameOverScreen.className = 'game-over-screen hidden';
    this.gameOverScreen.setAttribute('role', 'dialog');
    this.gameOverScreen.setAttribute('aria-label', 'Game Over');
    this.gameOverScreen.innerHTML = `
      <h1 class="result-title" id="result-title">Victory!</h1>
      <div class="result-message" id="result-message">You have defeated your opponent!</div>
      <div class="ornament"></div>
      <button class="menu-button" id="btn-play-again" aria-label="Fight Again - Start a new duel">Fight Again</button>
      <button class="menu-button" id="btn-main-menu" aria-label="Main Menu - Return to the main menu">Main Menu</button>
    `;
    this.container.appendChild(this.gameOverScreen);

    this.gameOverScreen.querySelector('#btn-play-again')?.addEventListener('click', () => {
      if (this.onPlayAgain) {
        this.onPlayAgain();
      }
    });

    this.gameOverScreen.querySelector('#btn-main-menu')?.addEventListener('click', () => {
      if (this.onMainMenu) {
        this.onMainMenu();
      }
    });
  }

  private createGestureIndicator(): void {
    this.gestureIndicator = document.createElement('div');
    this.gestureIndicator.className = 'gesture-indicator';
    this.gestureIndicator.textContent = 'Casting...';
    this.container.appendChild(this.gestureIndicator);
  }

  private showTournaments(): void {
    if (!this.tournamentUI) {
      this.tournamentUI = new TournamentUI(this.container);
      this.tournamentUI.setOnBackToMenu(() => {
        this.showScreen('menu');
      });
      this.tournamentUI.setOnNavigateToMatch((gameRoomId) => {
        // Navigate to the game room for this tournament match
        window.history.pushState({}, '', `/room/${gameRoomId}`);
        window.location.reload();
      });
    }
    // Hide menu screen before showing tournament
    this.menuScreen?.classList.add('hidden');
    this.wizardSelectScreen?.classList.add('hidden');
    this.gameOverScreen?.classList.add('hidden');
    this.tournamentUI.showTournamentBrowser();
  }

  private showHowToPlay(): void {
    // Lazily initialize camera for How to Play demonstration
    const webcamContainer = document.getElementById('webcam-container');

    // Enable practice mode for gesture feedback
    if (this.onSetPracticeMode) {
      this.onSetPracticeMode(true);
    }

    if (this.onInitCamera) {
      this.onInitCamera().then(success => {
        if (success && webcamContainer) {
          webcamContainer.style.display = 'block';
        }
      });
    } else if (webcamContainer) {
      webcamContainer.style.display = 'block';
    }

    const modal = document.createElement('div');
    modal.className = 'menu-screen';
    modal.style.background = 'rgba(0, 0, 0, 0.95)';
    modal.innerHTML = `
      <h2 class="game-title" style="font-size: 2rem;">How to Play</h2>
      <div class="ornament"></div>
      <div style="max-width: 600px; text-align: center; line-height: 1.8; margin: 1rem 0;">
        <p style="margin-bottom: 1rem;">Use hand gestures in front of your webcam to cast spells:</p>
        <p><strong style="color: #FF6B35;">\u{1F525} Fireball:</strong> Draw a circle with your finger</p>
        <p><strong style="color: #87CEEB;">\u{2744}\u{FE0F} Ice Shard:</strong> Swipe your hand downward quickly</p>
        <p><strong style="color: #C9A227;">\u{1F6E1}\u{FE0F} Shield:</strong> Hold an open palm steady</p>
        <p style="margin-top: 1rem; color: #44FF44;">Try the gestures now! The webcam will show success feedback.</p>
        <p style="margin-top: 0.5rem;">Reduce your opponent's health to zero to win!</p>
      </div>
      <button class="menu-button" id="btn-close-help">Got It!</button>
    `;
    this.container.appendChild(modal);

    modal.querySelector('#btn-close-help')?.addEventListener('click', () => {
      // Disable practice mode
      if (this.onSetPracticeMode) {
        this.onSetPracticeMode(false);
      }
      this.container.removeChild(modal);
      // Hide webcam when closing How to Play
      if (webcamContainer) {
        webcamContainer.style.display = 'none';
      }
    });
  }

  // --- Tutorial System ---

  private startTutorial(): void {
    this.menuScreen?.classList.add('hidden');

    this.tutorialManager.setCallbacks({
      onComplete: () => {
        this.menuScreen?.classList.remove('hidden');
      },
      onRequestCamera: () => {
        if (this.onInitCamera) {
          return this.onInitCamera();
        }
        return Promise.resolve(false);
      },
      onSetPracticeMode: (enabled: boolean) => {
        if (this.onSetPracticeMode) {
          this.onSetPracticeMode(enabled);
        }
      },
      onGestureConfirmSound: () => {
        if (this.onGestureConfirmSound) {
          this.onGestureConfirmSound();
        }
      }
    });

    this.tutorialManager.start();
  }

  /** Forward gesture detection to the tutorial when it is active */
  forwardGestureToTutorial(spell: SpellType, info?: { shape: GestureShape }): boolean {
    if (this.tutorialManager.isRunning()) {
      this.tutorialManager.onGestureDetected(spell, info);
      return true;
    }
    return false;
  }

  isTutorialActive(): boolean {
    return this.tutorialManager.isRunning();
  }

  // --- Gesture Reference Overlay ---

  private createGestureReference(): void {
    this.gestureReferenceOverlay = createGestureReferenceOverlay();
    this.container.appendChild(this.gestureReferenceOverlay);

    this.gestureHelpBtn = document.createElement('button');
    this.gestureHelpBtn.className = 'gesture-help-btn';
    this.gestureHelpBtn.id = 'gesture-help-btn';
    this.gestureHelpBtn.textContent = '?';
    this.gestureHelpBtn.setAttribute('aria-label', 'Toggle gesture reference');
    this.gestureHelpBtn.style.display = 'none';
    this.container.appendChild(this.gestureHelpBtn);

    this.gestureHelpBtn.addEventListener('click', () => {
      this.toggleGestureReference();
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === '?') {
        const state = this.gameState.getState();
        if (state.screen === 'playing') {
          this.toggleGestureReference();
        }
      }
    });
  }

  private toggleGestureReference(): void {
    if (!this.gestureReferenceOverlay) return;
    this.gestureReferenceOverlay.classList.toggle('hidden');
  }

  // --- State Management ---

  private onStateChange(state: GameStateData): void {
    this.updateHealthBars(state);

    if (state.screen === 'game-over') {
      this.showGameOver(state.winner === 'player');
    }

    // Show/hide gesture help button based on screen
    if (this.gestureHelpBtn) {
      this.gestureHelpBtn.style.display = state.screen === 'playing' ? 'flex' : 'none';
    }

    // Hide gesture reference when leaving gameplay
    if (state.screen !== 'playing' && this.gestureReferenceOverlay) {
      this.gestureReferenceOverlay.classList.add('hidden');
    }

    this.updateTitle(state.screen, state.winner ?? undefined);
  }

  private updateHealthBars(state: GameStateData): void {
    const playerHealthBar = document.getElementById('player-health-bar');
    const playerHealthText = document.getElementById('player-health-text');
    const playerHealthContainer = document.getElementById('player-health-container');
    const enemyHealthBar = document.getElementById('enemy-health-bar');
    const enemyHealthText = document.getElementById('enemy-health-text');
    const enemyHealthContainer = document.getElementById('enemy-health-container');

    if (playerHealthBar && playerHealthText) {
      const playerPercent = (state.player.health / state.player.maxHealth) * 100;
      playerHealthBar.style.width = `${playerPercent}%`;
      playerHealthText.textContent = `${Math.ceil(state.player.health)} / ${state.player.maxHealth}`;

      if (playerHealthContainer) {
        playerHealthContainer.setAttribute('aria-valuenow', String(Math.ceil(state.player.health)));
        playerHealthContainer.setAttribute('aria-valuemax', String(state.player.maxHealth));
      }

      if (playerPercent < 30) {
        playerHealthBar.classList.add('low');
      } else {
        playerHealthBar.classList.remove('low');
      }
    }

    if (enemyHealthBar && enemyHealthText) {
      const enemyPercent = (state.enemy.health / state.enemy.maxHealth) * 100;
      enemyHealthBar.style.width = `${enemyPercent}%`;
      enemyHealthText.textContent = `${Math.ceil(state.enemy.health)} / ${state.enemy.maxHealth}`;

      if (enemyHealthContainer) {
        enemyHealthContainer.setAttribute('aria-valuenow', String(Math.ceil(state.enemy.health)));
        enemyHealthContainer.setAttribute('aria-valuemax', String(state.enemy.maxHealth));
      }

      if (enemyPercent < 30) {
        enemyHealthBar.classList.add('low');
      } else {
        enemyHealthBar.classList.remove('low');
      }
    }
  }

  private startCooldownUpdate(): void {
    const updateCooldowns = () => {
      const spells: SpellType[] = ['fireball', 'iceShard', 'shield'];

      spells.forEach(spell => {
        const overlay = document.getElementById(`cd-${spell}`);
        const text = document.getElementById(`cd-text-${spell}`);
        const slot = overlay?.parentElement;

        if (!overlay || !text || !slot) return;

        const remaining = this.cooldownManager.getRemainingTime(spell);
        const progress = this.cooldownManager.getCooldownProgress(spell);

        if (remaining > 0) {
          overlay.style.height = `${(1 - progress) * 100}%`;
          text.textContent = Math.ceil(remaining / 1000).toString();
          text.style.display = 'block';
          slot.classList.remove('ready');
          slot.classList.add('cooldown');
        } else {
          overlay.style.height = '0%';
          text.style.display = 'none';
          slot.classList.add('ready');
          slot.classList.remove('cooldown');
        }
      });

      requestAnimationFrame(updateCooldowns);
    };

    updateCooldowns();
  }

  showScreen(screen: GameScreen): void {
    this.loadingScreen?.classList.add('hidden');
    this.menuScreen?.classList.add('hidden');
    this.wizardSelectScreen?.classList.add('hidden');
    this.gameOverScreen?.classList.add('hidden');
    this.multiplayerLobby?.classList.add('hidden');
    // Remove tournament screen when switching to other screens
    document.getElementById('tournament-screen')?.remove();

    // Hide countdown overlay on screen change
    this.countdownOverlay?.classList.add('hidden');

    // Show/hide combo meter based on screen
    if (screen === 'playing') {
      this.showComboMeter();
    } else {
      this.hideComboMeter();
    }

    if (this.hudElement) {
      this.hudElement.style.display = 'none';
    }

    // Hide webcam and model toggle on menu screens, show during gameplay
    const webcamContainer = document.getElementById('webcam-container');
    if (webcamContainer) {
      webcamContainer.style.display = screen === 'playing' ? 'block' : 'none';
    }
    const modelToggle = document.getElementById('model-toggle-btn');
    if (modelToggle) {
      modelToggle.style.display = screen === 'playing' ? 'block' : 'none';
    }

    switch (screen) {
      case 'loading':
        this.loadingScreen?.classList.remove('hidden');
        break;
      case 'menu':
        this.menuScreen?.classList.remove('hidden');
        break;
      case 'wizard-select':
        this.wizardSelectScreen?.classList.remove('hidden');
        break;
      case 'playing':
        if (this.hudElement) {
          this.hudElement.style.display = 'flex';
        }
        // Lazily initialize camera when gameplay starts
        if (this.onInitCamera) {
          this.onInitCamera().then(success => {
            if (!success) {
              // Hide webcam if camera init failed
              const webcamContainer = document.getElementById('webcam-container');
              if (webcamContainer) {
                webcamContainer.style.display = 'none';
              }
            }
          });
        }
        break;
      case 'game-over':
        break;
    }

    this.updateTitle(screen);
  }

  private showGameOver(victory: boolean): void {
    if (!this.gameOverScreen) return;

    const title = this.gameOverScreen.querySelector('#result-title');
    const message = this.gameOverScreen.querySelector('#result-message');

    if (title) {
      title.textContent = victory ? 'Victory!' : 'Defeat';
      title.className = `result-title ${victory ? 'victory' : 'defeat'}`;
    }

    if (message) {
      message.textContent = victory
        ? 'You have proven your arcane mastery!'
        : 'The enemy wizard has bested you...';
    }

    this.gameOverScreen.classList.remove('hidden');

    // Announce game result for screen readers
    this.announce(victory ? 'Victory! You have proven your arcane mastery!' : 'Defeat. The enemy wizard has bested you.');
  }

  private updateTitle(screen: GameScreen, winner?: 'player' | 'enemy'): void {
    const baseTitle = 'Wizard Duel';

    switch (screen) {
      case 'loading':
        document.title = `${baseTitle} - Loading...`;
        break;
      case 'menu':
        document.title = `${baseTitle} - Main Menu`;
        break;
      case 'wizard-select':
        document.title = `${baseTitle} - Select Wizard`;
        break;
      case 'playing':
        document.title = `${baseTitle} - In Battle`;
        break;
      case 'game-over':
        if (winner === 'player') {
          document.title = `${baseTitle} - Victory!`;
        } else if (winner === 'enemy') {
          document.title = `${baseTitle} - Defeat`;
        } else {
          document.title = `${baseTitle} - Game Over`;
        }
        break;
      default:
        document.title = baseTitle;
    }
  }

  showGestureIndicator(spell: SpellType, info?: GestureInfo): void {
    if (!this.gestureIndicator) return;

    const spellNames: Record<SpellType, string> = {
      fireball: '\u{1F525} Fireball!',
      iceShard: '\u{2744}\u{FE0F} Ice Shard!',
      lightningBolt: '\u{26A1} Lightning Bolt!',
      shield: '\u{1F6E1}\u{FE0F} Shield!',
      healSpell: '\u{1F49A} Heal!',
      reflect: '\u{1F300} Reflect!',
      teleport: '\u{2728} Teleport!',
      meteor: '\u{2604}\u{FE0F} Meteor!',
      superShield: '\u{1F6E1}\u{FE0F} Super Shield!',
      blizzard: '\u{2744}\u{FE0F} Blizzard!'
    };

    const shapeNames: Record<string, string> = {
      'circle': 'Circle',
      'swipe-down': 'Swipe Down',
      'palm-hold': 'Palm Hold',
      'zigzag': 'Zigzag',
      'swipe-up': 'Swipe Up'
    };

    // Build the indicator content
    let content = spellNames[spell];
    if (info) {
      content = `✓ ${spellNames[spell]}\n<span class="shape-detected">${shapeNames[info.shape]} detected</span>`;
    }

    this.gestureIndicator.innerHTML = content;
    this.gestureIndicator.classList.add('active');

    // Announce spell cast for screen readers
    const plainNames: Record<SpellType, string> = {
      fireball: 'Fireball cast',
      iceShard: 'Ice Shard cast',
      lightningBolt: 'Lightning Bolt cast',
      shield: 'Shield activated',
      healSpell: 'Heal cast',
      reflect: 'Reflect cast',
      teleport: 'Teleport cast',
      meteor: 'Meteor cast',
      superShield: 'Super Shield activated',
      blizzard: 'Blizzard cast'
    };
    this.announce(plainNames[spell]);

    // Flash webcam border green on success
    const webcamContainer = document.getElementById('webcam-container');
    if (webcamContainer) {
      webcamContainer.classList.add('gesture-success');
      setTimeout(() => {
        webcamContainer.classList.remove('gesture-success');
      }, 500);
    }

    setTimeout(() => {
      this.gestureIndicator?.classList.remove('active');
    }, 1000);
  }

  setCallbacks(callbacks: {
    onStartGame: () => void;
    onWizardSelect: (type: WizardType) => void;
    onPlayAgain: () => void;
    onMainMenu: () => void;
    onInitCamera?: () => Promise<boolean>;
    onSetPracticeMode?: (enabled: boolean) => void;
    onGestureConfirmSound?: () => void;
    onShowAchievements?: () => void;
    onShowCosmetics?: () => void;
    onShowFriends?: () => void;
    onShowShop?: () => void;
    onArenaThemeChange?: (themeId: ArenaThemeId) => void;
  }): void {
    this.onStartGame = callbacks.onStartGame;
    this.onWizardSelect = callbacks.onWizardSelect;
    this.onPlayAgain = callbacks.onPlayAgain;
    this.onMainMenu = callbacks.onMainMenu;
    this.onInitCamera = callbacks.onInitCamera || null;
    this.onSetPracticeMode = callbacks.onSetPracticeMode || null;
    this.onGestureConfirmSound = callbacks.onGestureConfirmSound || null;
    this.onShowAchievements = callbacks.onShowAchievements || null;
    this.onShowCosmetics = callbacks.onShowCosmetics || null;
    this.onShowFriends = callbacks.onShowFriends || null;
    this.onShowShop = callbacks.onShowShop || null;
    this.onArenaThemeChange = callbacks.onArenaThemeChange || null;
  }

  getSelectedWizard(): WizardType {
    return this.selectedWizard;
  }

  // Multiplayer UI Methods

  showMultiplayerLobby(role: PlayerRole, roomState: RoomState, roomId: string): void {
    // Update title for multiplayer
    document.title = 'Wizard Duel - Multiplayer Lobby';

    // Hide other screens
    this.loadingScreen?.classList.add('hidden');
    this.menuScreen?.classList.add('hidden');

    // Create or update lobby
    if (!this.multiplayerLobby) {
      this.multiplayerLobby = document.createElement('div');
      this.multiplayerLobby.className = 'menu-screen multiplayer-lobby';
      this.container.appendChild(this.multiplayerLobby);
    }

    const isPlayer1 = role === 'player1';
    const waitingForOpponent = isPlayer1 && !roomState.player2;

    this.multiplayerLobby.innerHTML = `
      <h1 class="game-title" style="font-size: 2rem;">Multiplayer Duel</h1>
      <div class="ornament"></div>
      <div class="room-info">
        <p>Room: <strong>${roomId}</strong></p>
        <button class="copy-link-btn" id="copy-room-link">Copy Room Link</button>
      </div>
      ${waitingForOpponent ? `
        <div class="waiting-message">
          <p>Waiting for opponent to join...</p>
          <div class="loading-spinner"></div>
        </div>
      ` : `
        <div class="players-status">
          <div class="player-slot ${roomState.player1?.ready ? 'ready' : ''}">
            <div class="player-label">Player 1 ${isPlayer1 ? '(You)' : ''}</div>
            <div class="player-wizard">${roomState.player1?.wizardType || 'Selecting...'}</div>
            <div class="ready-status">${roomState.player1?.ready ? 'Ready!' : 'Not Ready'}</div>
          </div>
          <div class="vs-divider">VS</div>
          <div class="player-slot ${roomState.player2?.ready ? 'ready' : ''}">
            <div class="player-label">Player 2 ${!isPlayer1 ? '(You)' : ''}</div>
            <div class="player-wizard">${roomState.player2?.wizardType || 'Selecting...'}</div>
            <div class="ready-status">${roomState.player2?.ready ? 'Ready!' : 'Not Ready'}</div>
          </div>
        </div>
        <h3 style="margin-top: 1.5rem;">Choose Your Wizard</h3>
        <div class="wizard-select" style="margin-bottom: 1rem;">
          ${Object.values(WIZARD_CONFIGS).map(config => `
            <div class="wizard-card" data-wizard="${config.type}">
              <div class="wizard-icon">${config.icon}</div>
              <div class="wizard-name">${config.name}</div>
            </div>
          `).join('')}
        </div>
        <button class="menu-button" id="btn-ready">I'm Ready!</button>
      `}
      <button class="menu-button" id="btn-leave" style="font-size: 0.9rem; padding: 0.6rem 1.5rem; margin-top: 1rem;">Leave Room</button>
    `;

    this.multiplayerLobby.classList.remove('hidden');

    // Event listeners
    this.multiplayerLobby.querySelector('#copy-room-link')?.addEventListener('click', () => {
      copyRoomUrl(roomId);
      const btn = this.multiplayerLobby?.querySelector('#copy-room-link');
      if (btn) btn.textContent = 'Copied!';
      setTimeout(() => {
        if (btn) btn.textContent = 'Copy Room Link';
      }, 2000);
    });

    const wizardCards = this.multiplayerLobby.querySelectorAll('.wizard-card');
    wizardCards.forEach(card => {
      card.addEventListener('click', () => {
        wizardCards.forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.selectedWizard = card.getAttribute('data-wizard') as WizardType;
        if (this.onWizardSelect) {
          this.onWizardSelect(this.selectedWizard);
        }
      });
    });

    // Select default wizard
    wizardCards[2]?.classList.add('selected');

    this.multiplayerLobby.querySelector('#btn-ready')?.addEventListener('click', () => {
      if (this.onStartGame) {
        this.onStartGame();
      }
    });

    this.multiplayerLobby.querySelector('#btn-leave')?.addEventListener('click', () => {
      if (this.onMainMenu) {
        this.onMainMenu();
      }
    });
  }

  showSpectatorMode(roomState: RoomState): void {
    // Update title for spectator mode
    document.title = 'Wizard Duel - Spectating';

    this.loadingScreen?.classList.add('hidden');
    this.menuScreen?.classList.add('hidden');

    if (!this.spectatorOverlay) {
      this.spectatorOverlay = document.createElement('div');
      this.spectatorOverlay.className = 'spectator-overlay';
      this.container.appendChild(this.spectatorOverlay);
    }

    const gameInProgress = roomState.gameStarted;

    this.spectatorOverlay.innerHTML = `
      <div class="spectator-badge">Spectating</div>
      ${gameInProgress ? '' : `
        <div class="spectator-waiting">
          <p>Waiting for players to start...</p>
          <p>Spectators: ${roomState.spectatorCount}</p>
        </div>
      `}
    `;

    this.spectatorOverlay.classList.remove('hidden');

    if (gameInProgress) {
      this.createCheerButtons();
    }
  }

  showSpectatorOverlay(): void {
    // Update title for spectator mode
    document.title = 'Wizard Duel - Spectating';

    if (!this.spectatorOverlay) {
      this.spectatorOverlay = document.createElement('div');
      this.spectatorOverlay.className = 'spectator-overlay';
      this.container.appendChild(this.spectatorOverlay);
    }

    this.spectatorOverlay.innerHTML = `<div class="spectator-badge">Spectating</div>`;
    this.spectatorOverlay.classList.remove('hidden');

    this.createCheerButtons();
  }

  private createCheerButtons(): void {
    if (this.cheerButtons) return;

    this.cheerButtons = document.createElement('div');
    this.cheerButtons.className = 'cheer-buttons';
    this.cheerButtons.innerHTML = `
      <button class="cheer-btn" data-cheer="applause">👏 Cheer</button>
      <button class="cheer-btn" data-cheer="wow">😮 Wow</button>
      <button class="cheer-btn" data-cheer="laugh">😂 Laugh</button>
      <button class="cheer-btn" data-cheer="boo">👎 Boo</button>
    `;
    this.container.appendChild(this.cheerButtons);

    this.cheerButtons.querySelectorAll('.cheer-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const cheerType = btn.getAttribute('data-cheer') as CheerType;
        if (this.onCheer) {
          this.onCheer(cheerType);
        }
      });
    });
  }

  showPlayerJoined(role: PlayerRole, name?: string): void {
    this.showToast(`${name || (role === 'player1' ? 'Player 1' : 'Player 2')} joined!`);
  }

  updateWizardSelection(player: PlayerRole, wizardType: string): void {
    const slot = this.multiplayerLobby?.querySelector(
      player === 'player1' ? '.player-slot:first-child .player-wizard' : '.player-slot:last-child .player-wizard'
    );
    if (slot) {
      slot.textContent = wizardType;
    }
  }

  updatePlayerReady(player: PlayerRole): void {
    const slot = this.multiplayerLobby?.querySelector(
      player === 'player1' ? '.player-slot:first-child' : '.player-slot:last-child'
    );
    if (slot) {
      slot.classList.add('ready');
      const readyStatus = slot.querySelector('.ready-status');
      if (readyStatus) readyStatus.textContent = 'Ready!';
    }
  }

  updateSpectatorCount(count: number): void {
    const badge = this.spectatorOverlay?.querySelector('.spectator-badge');
    if (badge) {
      badge.textContent = `Spectating (${count} viewers)`;
    }
  }

  showPlayerDisconnected(player: PlayerRole): void {
    this.showToast(`${player === 'player1' ? 'Player 1' : 'Player 2'} disconnected!`);
  }

  showSlowEffect(target: PlayerRole): void {
    this.showToast(`${target === 'player1' ? 'Player 1' : 'Player 2'} is slowed!`, 'ice');
  }

  showMultiplayerGameOver(winner: PlayerRole): void {
    this.showToast(`${winner === 'player1' ? 'Player 1' : 'Player 2'} wins!`, 'victory');
  }

  showCheerIndicator(from: string, cheerType: CheerType): void {
    const indicator = document.createElement('div');
    indicator.className = 'cheer-indicator';

    const emojis: Record<CheerType, string> = {
      applause: '👏',
      wow: '😮',
      laugh: '😂',
      boo: '👎'
    };

    indicator.innerHTML = `${emojis[cheerType]} ${from}`;
    this.container.appendChild(indicator);

    // Animate and remove
    setTimeout(() => {
      indicator.classList.add('fade-out');
      setTimeout(() => indicator.remove(), 500);
    }, 2000);
  }

  private showToast(message: string, type: 'default' | 'ice' | 'victory' = 'default'): void {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    this.container.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('fade-out');
      setTimeout(() => toast.remove(), 500);
    }, 3000);
  }

  setCheerCallback(callback: (type: CheerType) => void): void {
    this.onCheer = callback;
  }

  setEmoteCallback(callback: (type: EmoteType) => void): void {
    this.onEmote = callback;
  }

  // Create emote buttons panel for in-match use
  showEmotePanel(): void {
    if (this.emotePanel) return;

    this.emotePanel = document.createElement('div');
    this.emotePanel.className = 'emote-panel';
    this.emotePanel.innerHTML = `
      <button class="emote-toggle-btn" aria-label="Show emotes">
        <span class="emote-toggle-icon">\uD83D\uDCAC</span>
      </button>
      <div class="emote-buttons hidden">
        ${EMOTE_CONFIGS.map(
          (e) =>
            `<button class="emote-btn" data-emote="${e.type}" title="${e.label}">${e.icon}</button>`,
        ).join('')}
      </div>
    `;
    this.container.appendChild(this.emotePanel);

    const toggleBtn = this.emotePanel.querySelector('.emote-toggle-btn');
    const buttons = this.emotePanel.querySelector('.emote-buttons');
    toggleBtn?.addEventListener('click', () => {
      buttons?.classList.toggle('hidden');
    });

    this.emotePanel.querySelectorAll('.emote-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const emoteType = btn.getAttribute('data-emote') as EmoteType;
        if (this.onEmote) {
          this.onEmote(emoteType);
        }
        buttons?.classList.add('hidden');
      });
    });
  }

  hideEmotePanel(): void {
    if (this.emotePanel) {
      this.emotePanel.remove();
      this.emotePanel = null;
    }
  }

  showEmoteIndicator(from: string, emoteType: EmoteType): void {
    const labels: Record<EmoteType, string> = {
      well_played: '\uD83D\uDC4F Well Played!',
      nice_combo: '\uD83D\uDD25 Nice Combo!',
      gg: '\uD83E\uDD1D GG!',
      wow_emote: '\uD83D\uDE2E Wow!',
    };

    const indicator = document.createElement('div');
    indicator.className = 'emote-indicator';
    indicator.innerHTML = `<span class="emote-from">${from}:</span> ${labels[emoteType] || emoteType}`;
    this.container.appendChild(indicator);

    setTimeout(() => {
      indicator.classList.add('fade-out');
      setTimeout(() => indicator.remove(), 500);
    }, 2000);
  }

  async showFriendList(friendClient: FriendClient): Promise<void> {
    if (this.friendListModal) {
      this.friendListModal.remove();
    }

    this.friendListModal = document.createElement('div');
    this.friendListModal.className = 'menu-screen';
    this.friendListModal.style.background = 'rgba(0, 0, 0, 0.95)';
    this.friendListModal.setAttribute('role', 'dialog');
    this.friendListModal.setAttribute('aria-label', 'Friends');

    this.friendListModal.innerHTML = `
      <h2 class="game-title" style="font-size: 2rem;">Friends</h2>
      <div class="ornament"></div>
      <div class="friend-code-section">
        <div class="friend-code-label">Your Friend Code:</div>
        <div class="friend-code-value" id="my-friend-code">Loading...</div>
        <button class="copy-link-btn" id="copy-friend-code">Copy</button>
      </div>
      <div class="friend-add-section">
        <input type="text" id="friend-code-input" class="friend-code-input" placeholder="Enter friend code" maxlength="6" />
        <button class="menu-button friend-add-btn" id="btn-add-friend">Add Friend</button>
      </div>
      <div id="friend-add-status" class="friend-status-message"></div>
      <div id="friend-pending-section" class="friend-section" style="display: none;">
        <h3 class="friend-section-title">Pending Requests</h3>
        <div id="friend-pending-list">Loading...</div>
      </div>
      <div class="friend-section">
        <h3 class="friend-section-title">Friends</h3>
        <div id="friend-list">Loading...</div>
      </div>
      <button class="menu-button" id="btn-close-friends" style="margin-top: 1.5rem;">Back</button>
    `;
    this.container.appendChild(this.friendListModal);

    // Load friend code
    try {
      const code = await friendClient.getFriendCode();
      const codeEl = this.friendListModal.querySelector('#my-friend-code');
      if (codeEl) codeEl.textContent = code;
    } catch {
      const codeEl = this.friendListModal.querySelector('#my-friend-code');
      if (codeEl) codeEl.textContent = 'Error';
    }

    // Copy friend code
    this.friendListModal.querySelector('#copy-friend-code')?.addEventListener('click', async () => {
      const codeEl = this.friendListModal?.querySelector('#my-friend-code');
      const code = codeEl?.textContent || '';
      if (code && code !== 'Loading...' && code !== 'Error') {
        await navigator.clipboard.writeText(code).catch(() => {});
        const btn = this.friendListModal?.querySelector('#copy-friend-code');
        if (btn) {
          btn.textContent = 'Copied!';
          setTimeout(() => { if (btn) btn.textContent = 'Copy'; }, 2000);
        }
      }
    });

    // Add friend handler
    this.friendListModal.querySelector('#btn-add-friend')?.addEventListener('click', async () => {
      const input = this.friendListModal?.querySelector('#friend-code-input') as HTMLInputElement;
      const statusEl = this.friendListModal?.querySelector('#friend-add-status');
      if (!input || !statusEl) return;

      const code = input.value.trim().toUpperCase();
      if (!code || code.length !== 6) {
        statusEl.textContent = 'Please enter a 6-character friend code';
        statusEl.className = 'friend-status-message error';
        return;
      }

      try {
        const result = await friendClient.sendFriendRequest(code);
        if (result.error) {
          statusEl.textContent = result.error;
          statusEl.className = 'friend-status-message error';
        } else if (result.status === 'accepted') {
          statusEl.textContent = `Now friends with ${result.friendName}!`;
          statusEl.className = 'friend-status-message success';
          input.value = '';
          this.refreshFriendList(friendClient);
        } else {
          statusEl.textContent = `Friend request sent to ${result.friendName}!`;
          statusEl.className = 'friend-status-message success';
          input.value = '';
        }
      } catch {
        statusEl.textContent = 'Failed to send request';
        statusEl.className = 'friend-status-message error';
      }
    });

    // Close handler
    this.friendListModal.querySelector('#btn-close-friends')?.addEventListener('click', () => {
      this.friendListModal?.remove();
      this.friendListModal = null;
    });

    // Load data
    this.refreshFriendList(friendClient);
    this.refreshPendingRequests(friendClient);
  }

  private async refreshFriendList(friendClient: FriendClient): Promise<void> {
    const listEl = this.friendListModal?.querySelector('#friend-list');
    if (!listEl) return;

    try {
      const friends = await friendClient.listFriends();
      if (friends.length === 0) {
        listEl.innerHTML = '<div class="friend-empty">No friends yet. Share your code!</div>';
        return;
      }

      listEl.innerHTML = friends
        .map(
          (f: FriendInfo) => `
        <div class="friend-row">
          <div class="friend-info">
            <span class="friend-name">${this.escapeHtml(f.name)}</span>
            <span class="friend-status ${f.online ? 'online' : 'offline'}">
              ${f.online ? (f.inMatch ? 'In Match' : 'Online') : 'Offline'}
            </span>
          </div>
          <div class="friend-actions">
            ${f.online && f.inMatch && f.roomId ? `<button class="friend-action-btn spectate-btn" data-room="${f.roomId}">Spectate</button>` : ''}
            ${f.online && !f.inMatch ? `<button class="friend-action-btn challenge-btn" data-friend-id="${f.playerId}">Challenge</button>` : ''}
            <button class="friend-action-btn remove-btn" data-friend-id="${f.playerId}">Remove</button>
          </div>
        </div>
      `,
        )
        .join('');

      listEl.querySelectorAll('.spectate-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const roomId = btn.getAttribute('data-room');
          if (roomId) {
            navigateToRoom(roomId);
            window.location.reload();
          }
        });
      });

      listEl.querySelectorAll('.challenge-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const roomId = generateRoomId();
          navigateToRoom(roomId);
          window.location.reload();
        });
      });

      listEl.querySelectorAll('.remove-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const friendId = btn.getAttribute('data-friend-id');
          if (friendId) {
            await friendClient.removeFriend(friendId);
            this.refreshFriendList(friendClient);
          }
        });
      });
    } catch {
      listEl.innerHTML = '<div class="friend-empty">Failed to load friends</div>';
    }
  }

  private async refreshPendingRequests(friendClient: FriendClient): Promise<void> {
    const section = this.friendListModal?.querySelector('#friend-pending-section');
    const listEl = this.friendListModal?.querySelector('#friend-pending-list');
    if (!section || !listEl) return;

    try {
      const pending = await friendClient.getPendingRequests();
      if (pending.length === 0) {
        (section as HTMLElement).style.display = 'none';
        return;
      }

      (section as HTMLElement).style.display = 'block';
      listEl.innerHTML = pending
        .map(
          (p: PendingRequest) => `
        <div class="friend-row pending">
          <div class="friend-info">
            <span class="friend-name">${this.escapeHtml(p.name)}</span>
            <span class="friend-code-small">${p.friendCode}</span>
          </div>
          <div class="friend-actions">
            <button class="friend-action-btn accept-btn" data-friend-id="${p.playerId}">Accept</button>
            <button class="friend-action-btn remove-btn" data-friend-id="${p.playerId}">Reject</button>
          </div>
        </div>
      `,
        )
        .join('');

      listEl.querySelectorAll('.accept-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const friendId = btn.getAttribute('data-friend-id');
          if (friendId) {
            await friendClient.acceptRequest(friendId);
            this.refreshPendingRequests(friendClient);
            this.refreshFriendList(friendClient);
          }
        });
      });

      listEl.querySelectorAll('.remove-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const friendId = btn.getAttribute('data-friend-id');
          if (friendId) {
            await friendClient.removeFriend(friendId);
            this.refreshPendingRequests(friendClient);
          }
        });
      });
    } catch {
      listEl.innerHTML = '<div class="friend-empty">Failed to load requests</div>';
    }
  }

  private selectDefaultThemeCard(themeCards: NodeListOf<Element>): void {
    // Determine the current theme from the manager (deferred until callbacks are set)
    const savedTheme = localStorage.getItem('wizard_duel_arena_theme') || 'classic';
    themeCards.forEach(tc => {
      if (tc.getAttribute('data-theme') === savedTheme) {
        tc.classList.add('selected');
        tc.setAttribute('aria-checked', 'true');
      }
    });
    // If none matched (shouldn't happen), select classic
    const anySelected = Array.from(themeCards).some(tc => tc.classList.contains('selected'));
    if (!anySelected && themeCards.length > 0) {
      themeCards[0].classList.add('selected');
      themeCards[0].setAttribute('aria-checked', 'true');
    }
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // Add multiplayer button to main menu
  addMultiplayerButton(): void {
    const playBtn = this.menuScreen?.querySelector('#btn-play');
    if (playBtn && !this.menuScreen?.querySelector('#btn-multiplayer')) {
      const mpBtn = document.createElement('button');
      mpBtn.className = 'menu-button';
      mpBtn.id = 'btn-multiplayer';
      mpBtn.textContent = 'Multiplayer';
      playBtn.after(mpBtn);

      mpBtn.addEventListener('click', () => {
        const roomId = generateRoomId();
        navigateToRoom(roomId);
        window.location.reload();
      });
    }
  }

  // Achievement toast notification
  showAchievementToast(name: string, description: string): void {
    const toast = document.createElement('div');
    toast.className = 'achievement-toast';
    toast.innerHTML = `
      <div class="achievement-toast-icon">&#x2728;</div>
      <div class="achievement-toast-content">
        <div class="achievement-toast-title">Achievement Unlocked!</div>
        <div class="achievement-toast-name">${name}</div>
        <div class="achievement-toast-desc">${description}</div>
      </div>
    `;
    this.container.appendChild(toast);

    // Announce for screen readers
    this.announce(`Achievement unlocked: ${name}. ${description}`);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      toast.classList.add('achievement-toast-exit');
      setTimeout(() => toast.remove(), 500);
    }, 4000);
  }

  // Achievement gallery modal
  showAchievementGallery(achievementManager: AchievementManager): void {
    const defs = achievementManager.getDefinitions();
    const modal = document.createElement('div');
    modal.className = 'menu-screen';
    modal.style.background = 'rgba(0, 0, 0, 0.95)';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-label', 'Achievement Gallery');

    const cards = defs.map(def => {
      const progress = achievementManager.getProgress(def.id);
      const unlocked = progress?.unlocked ?? false;
      const current = progress?.progress ?? 0;
      const pct = Math.min(100, Math.round((current / def.maxProgress) * 100));

      return `
        <div class="achievement-card ${unlocked ? 'unlocked' : 'locked'}">
          <div class="achievement-card-icon">${unlocked ? def.icon : '&#x1F512;'}</div>
          <div class="achievement-card-name">${def.name}</div>
          <div class="achievement-card-desc">${def.description}</div>
          <div class="achievement-progress-bar-bg">
            <div class="achievement-progress-bar-fill" style="width: ${pct}%"></div>
          </div>
          <div class="achievement-card-progress">${current} / ${def.maxProgress}</div>
        </div>
      `;
    }).join('');

    modal.innerHTML = `
      <h2 class="game-title" style="font-size: 2rem;">Achievements</h2>
      <div class="ornament"></div>
      <div class="achievement-grid">${cards}</div>
      <button class="menu-button" id="btn-close-achievements" style="margin-top: 1.5rem;">Back</button>
    `;
    this.container.appendChild(modal);

    modal.querySelector('#btn-close-achievements')?.addEventListener('click', () => {
      this.container.removeChild(modal);
    });
  }

  // Cosmetic selector modal
  showCosmeticSelector(cosmeticManager: CosmeticManager): void {
    const defs = cosmeticManager.getDefinitions();
    const modal = document.createElement('div');
    modal.className = 'menu-screen';
    modal.style.background = 'rgba(0, 0, 0, 0.95)';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-label', 'Cosmetics');

    const renderCards = () => {
      return defs.map(def => {
        const unlocked = cosmeticManager.isUnlocked(def.id);
        const equipped = cosmeticManager.getEquipped(def.type) === def.id;
        const unlockDesc = cosmeticManager.getUnlockDescription(def.unlockCondition);

        return `
          <div class="cosmetic-card ${unlocked ? 'unlocked' : 'locked'} ${equipped ? 'equipped' : ''}" data-cosmetic-id="${def.id}">
            <div class="cosmetic-card-preview">${def.preview}</div>
            <div class="cosmetic-card-name">${def.name}</div>
            <div class="cosmetic-card-desc">${def.description}</div>
            ${unlocked
              ? `<button class="cosmetic-equip-btn" data-action="${equipped ? 'unequip' : 'equip'}" data-id="${def.id}" data-type="${def.type}">
                   ${equipped ? 'Unequip' : 'Equip'}
                 </button>`
              : `<div class="cosmetic-card-lock">${unlockDesc}</div>`
            }
          </div>
        `;
      }).join('');
    };

    const updateModal = () => {
      const grid = modal.querySelector('.cosmetic-grid');
      if (grid) {
        grid.innerHTML = renderCards();
        attachButtonListeners();
      }
    };

    const attachButtonListeners = () => {
      modal.querySelectorAll('.cosmetic-equip-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const action = btn.getAttribute('data-action');
          const id = btn.getAttribute('data-id')!;
          const type = btn.getAttribute('data-type') as CosmeticType;
          if (action === 'equip') {
            cosmeticManager.equip(id);
          } else {
            cosmeticManager.unequip(type);
          }
          updateModal();
        });
      });
    };

    modal.innerHTML = `
      <h2 class="game-title" style="font-size: 2rem;">Cosmetics</h2>
      <div class="ornament"></div>
      <div class="cosmetic-grid">${renderCards()}</div>
      <button class="menu-button" id="btn-close-cosmetics" style="margin-top: 1.5rem;">Back</button>
    `;
    this.container.appendChild(modal);
    attachButtonListeners();

    modal.querySelector('#btn-close-cosmetics')?.addEventListener('click', () => {
      this.container.removeChild(modal);
    });
  }

  // --- Countdown Overlay ---

  private createCountdownOverlay(): void {
    this.countdownOverlay = document.createElement('div');
    this.countdownOverlay.className = 'countdown-overlay hidden';
    this.countdownOverlay.setAttribute('aria-live', 'assertive');
    this.countdownOverlay.setAttribute('aria-atomic', 'true');
    this.container.appendChild(this.countdownOverlay);
  }

  showCountdown(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.countdownOverlay) {
        resolve();
        return;
      }

      this.countdownOverlay.classList.remove('hidden');
      const steps = [
        { text: '3', className: 'countdown-text', delay: 800 },
        { text: '2', className: 'countdown-text', delay: 800 },
        { text: '1', className: 'countdown-text', delay: 800 },
        { text: 'FIGHT!', className: 'countdown-text countdown-text--fight', delay: 500 },
      ];

      let elapsed = 0;
      steps.forEach((step) => {
        setTimeout(() => {
          if (!this.countdownOverlay) return;
          this.countdownOverlay.innerHTML = `<div class="${step.className}">${step.text}</div>`;
        }, elapsed);
        elapsed += step.delay;
      });

      // After all steps, hide and resolve
      setTimeout(() => {
        this.countdownOverlay?.classList.add('hidden');
        if (this.countdownOverlay) {
          this.countdownOverlay.innerHTML = '';
        }
        resolve();
      }, elapsed);
    });
  }

  // --- Combo Meter ---

  private createComboUI(): void {
    // Combo meter bar
    this.comboMeterContainer = document.createElement('div');
    this.comboMeterContainer.className = 'combo-meter-container';

    this.comboMeterWrapper = document.createElement('div');
    this.comboMeterWrapper.className = 'combo-meter-bar-wrapper';

    this.comboMeterFill = document.createElement('div');
    this.comboMeterFill.className = 'combo-meter-fill';

    this.comboMeterBonus = document.createElement('div');
    this.comboMeterBonus.className = 'combo-meter-bonus';

    this.comboMeterWrapper.appendChild(this.comboMeterFill);
    this.comboMeterContainer.appendChild(this.comboMeterWrapper);
    this.comboMeterContainer.appendChild(this.comboMeterBonus);
    this.container.appendChild(this.comboMeterContainer);

    // Combo popup
    this.comboPopup = document.createElement('div');
    this.comboPopup.className = 'combo-popup';
    this.container.appendChild(this.comboPopup);
  }

  updateComboMeter(meter: number): void {
    if (!this.comboMeterFill || !this.comboMeterBonus || !this.comboMeterWrapper) return;

    this.comboMeterFill.style.width = `${Math.min(meter, 100)}%`;

    // Tier classes
    this.comboMeterFill.classList.remove('tier-1', 'tier-2', 'tier-max');
    if (meter >= 100) {
      this.comboMeterFill.classList.add('tier-max');
    } else if (meter >= 75) {
      this.comboMeterFill.classList.add('tier-2');
    } else if (meter >= 50) {
      this.comboMeterFill.classList.add('tier-1');
    }

    // Pulse when above tier 1
    if (meter >= 50) {
      this.comboMeterWrapper.classList.add('pulsing');
    } else {
      this.comboMeterWrapper.classList.remove('pulsing');
    }

    // Bonus text
    if (meter >= 75) {
      this.comboMeterBonus.textContent = '1.15x';
    } else if (meter >= 50) {
      this.comboMeterBonus.textContent = '1.1x';
    } else {
      this.comboMeterBonus.textContent = '';
    }
  }

  showComboPopup(comboResult: ComboResult): void {
    if (!this.comboPopup) return;

    // Clear any existing timeout
    if (this.comboPopupTimeout) {
      clearTimeout(this.comboPopupTimeout);
    }

    const comboIcons: Record<string, string> = {
      inferno: '\u{1F525}',
      steamCloud: '\u{1F32B}\u{FE0F}',
      shieldCounter: '\u{1F6E1}\u{FE0F}\u{26A1}',
    };

    const icon = comboIcons[comboResult.id] || '\u{2728}';

    this.comboPopup.innerHTML = `
      <div class="combo-popup-name">${icon} ${comboResult.name}!</div>
      <div class="combo-popup-multiplier">${comboResult.damageMultiplier}x damage</div>
    `;

    // Trigger enter animation
    this.comboPopup.classList.remove('exiting');
    this.comboPopup.classList.add('entering');

    // Hold for 1.5s, then fade out
    this.comboPopupTimeout = setTimeout(() => {
      if (!this.comboPopup) return;
      this.comboPopup.classList.remove('entering');
      this.comboPopup.classList.add('exiting');

      this.comboPopupTimeout = setTimeout(() => {
        if (this.comboPopup) {
          this.comboPopup.classList.remove('exiting');
          this.comboPopup.innerHTML = '';
        }
      }, 300);
    }, 1500);

    // Announce for screen readers
    this.announce(`Combo: ${comboResult.name}! ${comboResult.damageMultiplier}x damage`);
  }

  showComboMeter(): void {
    this.comboMeterContainer?.classList.add('visible');
  }

  hideComboMeter(): void {
    this.comboMeterContainer?.classList.remove('visible');
    this.updateComboMeter(0);
  }
}
